import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Text,
  Spinner,
  Flex,
  Badge,
  Icon,
  Center,
  VStack,
} from '@chakra-ui/react';
import { FiTruck } from 'react-icons/fi';
import { MdLockOutline } from 'react-icons/md';

import { CartContext } from '@repo/contexts';
import hiddenMavenoid from '@repo/ui-components/dept/utils/hiddenMavenoid';
import Link from '@repo/ui-components/tailwind/Link';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { pushToDataLayer } from '@repo/utils';
import CartItem from './CartItem';

function Cart({ freeShippingThreshold }) {
  const {
    cart,
    cartActive,
    setCartActive,
    cartBusy,
    incrementLineItem,
    decrementLineItem,
    deleteLineItem,
  } = useContext(CartContext);
  const { tt, localizePrice } = useUiComponentsInternationalContext();

  const { lines, cost, checkoutUrl } = cart;

  const hasSubscriptionProduct = lines?.edges?.find(
    ({ node }) => node?.sellingPlanAllocation?.sellingPlan?.id
  );
  const isFreeShipping =
    cost?.subtotalAmount?.amount > freeShippingThreshold ||
    hasSubscriptionProduct;

  const hasItems = lines?.edges?.length > 0;
  const checkoutIsDisabled =
    !hasItems || cost?.subtotalAmount?.amount === 0 || cartBusy;

  const totals = [
    {
      label: tt(s => s.cartDeptComponents.subtotalLabel),
      value: cost?.subtotalAmount?.amount,
    },
    {
      label: tt(s => s.cartDeptComponents.estimatedShippingLabel),
      value: 9.95,
      freeShipping: isFreeShipping,
    },
  ];

  useEffect(() => {
    hiddenMavenoid(cartActive);
  }, [cartActive]);

  useEffect(() => {
    if (cartActive) {
      pushToDataLayer('view_cart', {
        ecommerce: {
          currency: cost?.subtotalAmount?.currencyCode,
          value: cost?.subtotalAmount?.amount,
        },
        items:
          lines?.edges?.map(edge => ({
            variant: edge.node?.merchandise,
            product: edge.node?.merchandise?.product,
          })) ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartActive]);

  return (
    <Drawer
      isOpen={cartActive}
      placement="right"
      onClose={() => setCartActive(false)}
      zIndex="tooltip"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton
          border="1px solid"
          borderColor="typography.base"
          color="typography.base"
          p={4}
          sx={{
            svg: { width: 3, height: 3 },
          }}
        />

        <DrawerHeader>
          <Flex>
            <Text textStyle="h4_5_6">
              {tt(s => s.cartDeptComponents.myCartLabel)}{' '}
              {cartBusy ? (
                <Spinner pos="relative" h="4" w="4" />
              ) : (
                `(${cart?.totalQuantity || 0})`
              )}
            </Text>
          </Flex>
        </DrawerHeader>

        <DrawerBody as={Flex} direction="column" align="center" gap={4} p={0}>
          <VStack w="full" spacing={4} px={4} pt={2} flex={1} pos="relative">
            {freeShippingThreshold && (
              <Flex
                as={Badge}
                gap={4}
                py={2}
                px={4}
                align="center"
                justify="space-between"
                w="full"
                whiteSpace="break-spaces"
              >
                <Text textStyle="caption" fontWeight="bold" color="white">
                  {isFreeShipping
                    ? tt(s => s.cartDeptComponents.freeShippingLabel)
                    : tt(s => s.cartDeptComponents.notFreeShippingYetLabel, {
                        freeShippingThreshold: localizePrice(
                          freeShippingThreshold,
                          0
                        ),
                      })}
                </Text>
                <Icon w={8} h={8} as={FiTruck} color="brand.sunny" />
              </Flex>
            )}
            <Flex
              direction="column"
              h="full"
              w="full"
              mx={4}
              sx={{
                '& > *:last-child': { borderBottom: 'none' },
              }}
            >
              {!hasItems && (
                <Center flexDir="column" h="full" gap={2}>
                  <Text>{tt(s => s.cartDeptComponents.noItemsMessage)}</Text>
                  <Button
                    variant="link"
                    fontSize="1rem"
                    color="brand.ink"
                    onClick={() => setCartActive(false)}
                  >
                    {tt(s => s.cartDeptComponents.continueShoppingButton)}
                  </Button>
                </Center>
              )}
              {lines?.edges?.map(
                ({
                  node: {
                    id,
                    merchandise,
                    quantity,
                    attributes,
                    sellingPlanAllocation,
                  },
                }) => (
                  <CartItem
                    key={id}
                    id={id}
                    variant={merchandise}
                    quantity={quantity}
                    title={merchandise.product.title}
                    incrementLineItem={incrementLineItem}
                    decrementLineItem={decrementLineItem}
                    deleteLineItem={deleteLineItem}
                    attributes={attributes}
                    sellingPlanAllocation={sellingPlanAllocation}
                  />
                )
              )}
            </Flex>
          </VStack>

          {hasItems && (
            <VStack
              w="full"
              bg="neutral.light"
              p={6}
              pt={2}
              spacing={2}
              sx={{
                '& > *:last-child': { borderBottom: 'none' },
              }}
            >
              {totals.map(({ label, value, freeShipping }) => (
                <Flex
                  key={label}
                  justify="space-between"
                  w="full"
                  borderBottom="1px solid"
                  borderColor="stroke.light"
                  py={2}
                >
                  <Text textStyle="caption">{label}</Text>
                  <Text textStyle="caption">
                    {freeShipping ? (
                      <Text fontWeight="bold" color="alerts.success.base">
                        {tt(s => s.cartDeptComponents.freeLabel)}
                      </Text>
                    ) : (
                      localizePrice(value || 0)
                    )}
                  </Text>
                </Flex>
              ))}
            </VStack>
          )}
        </DrawerBody>

        {hasItems && (
          <DrawerFooter boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)">
            <Button
              as={Link}
              className="w-full"
              link={checkoutUrl}
              isLoading={cartBusy}
              isDisabled={checkoutIsDisabled}
            >
              <Icon w={4} h={4} as={MdLockOutline} />
              {tt(s => s.cartDeptComponents.checkoutButton)}
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}

Cart.propTypes = {
  freeShippingThreshold: PropTypes.number.isRequired,
};

export default Cart;
